import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const STAGE_LABELS = {
  order_placed:      "Order Placed",
  payment_confirmed: "Payment Confirmed",
  packaged:          "Packaged",
  shipped:           "Shipped",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Delivered"
};

const card = {
  borderRadius: 24,
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(24px)",
  border: "1px solid rgba(255,255,255,0.82)",
  boxShadow: "0 20px 50px rgba(24,35,60,0.09)",
  padding: "20px 18px",
  marginBottom: 14,
};

function SectionTitle({ title }) {
  return (
    <p style={{ margin: "0 0 14px", fontSize: 11, fontWeight: 900, color: "#70747e", textTransform: "uppercase", letterSpacing: 2.2 }}>
      {title}
    </p>
  );
}

function ActionRow({ title, subtitle, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: "100%", border: "none", background: "transparent",
      padding: "16px 0", borderTop: "1px solid rgba(0,0,0,0.06)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      textAlign: "left", cursor: "pointer"
    }}>
      <div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#050505" }}>{title}</p>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#858995" }}>{subtitle}</p>
      </div>
      <span style={{ fontSize: 28, color: "#b0b4bd", lineHeight: 1 }}>›</span>
    </button>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
      <span style={{ fontSize: 15, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 15, color: "#777b85", textAlign: "right", maxWidth: "55%" }}>{value}</span>
    </div>
  );
}

export default function AccountScreenMobile() {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [orderCount, setOrderCount] = useState(0);
  const [activeOrder, setActiveOrder] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.id) {
      fetch(`https://eleven11-i6v4.onrender.com/api/orders?userId=${user.id}&email=${encodeURIComponent(user.email)}`)
        .then(r => r.json())
        .then(data => {
          const orders = data.orders || [];
          setOrderCount(orders.length);
          const active = orders.find(o => o.fulfillmentStatus !== "delivered" && o.items?.length > 0);
          setActiveOrder(active || null);
        })
        .catch(() => {});
    }
  }, [user]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f7fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 15, color: "#9CA3AF", fontWeight: 600 }}>Loading account...</div>
      </div>
    );
  }

  if (!user) return null;

  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "Active member";

  const trackingValue = activeOrder ? STAGE_LABELS[activeOrder.fulfillmentStatus] || "In Progress" : "—";
  const trackingSubtext = activeOrder ? activeOrder.orderNumber : "No active shipments";
  const trackingClick = activeOrder ? () => navigate(`/account/orders/${activeOrder._id}`) : undefined;

  const phoneFormatted = user.phone
    ? `(${user.phone.replace(/\D/g,"").slice(0,3)}) ${user.phone.replace(/\D/g,"").slice(3,6)}-${user.phone.replace(/\D/g,"").slice(6)}`
    : "—";

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg,#f5f7fb 0%,#eef1f7 45%,#f7f8fb 100%)", fontFamily: "-apple-system, BlinkMacSystemFont, Inter, system-ui", paddingBottom: 50 }}>
      <div style={{ padding: "24px 16px 0" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: 2.4, color: "#7b7f89" }}>ELEVEN ACCOUNT</p>
            <h1 style={{ margin: "4px 0 0", fontSize: 34, fontWeight: 800, letterSpacing: -1 }}>Profile</h1>
          </div>
          <button
            onClick={async () => { localStorage.removeItem("user"); await logout(); navigate("/"); }}
            style={{ border: "1px solid rgba(0,0,0,0.08)", background: "rgba(255,255,255,0.75)", borderRadius: 999, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            Logout
          </button>
        </div>

        {/* Profile Card */}
        <div style={{ ...card, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(145deg,#f6f7fa,#e6e9ef)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 800, color: "#111", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)" }}>
              {user.firstName?.[0] || "E"}
            </div>
            <div style={{ position: "absolute", right: 0, bottom: 4, width: 22, height: 22, borderRadius: "50%", background: "#0a84ff", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, border: "2px solid white" }}>✓</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: -0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {fullName || "ELEVEN User"}
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#777b85", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user.email}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <span style={{ background: "#050505", color: "#fff", borderRadius: 999, padding: "4px 10px", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>
                {user.role === "admin" ? "Admin" : "Customer"}
              </span>
              <span style={{ color: "#8a8e98", fontSize: 12, fontWeight: 600 }}>{memberSince}</span>
            </div>
          </div>
        </div>

        {/* Info Grid — 2x2 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          {[
            { label: "Orders", value: String(orderCount), subtext: "Completed purchases", onClick: () => navigate("/account/orders") },
            { label: "Tracking", value: trackingValue, subtext: trackingSubtext, onClick: trackingClick },
            { label: "Store Credit", value: `$${Number(user.storeCredit || 0).toFixed(2)}`, subtext: "Available balance" },
            { label: "Account ID", value: user.id?.slice(-6) || "—", subtext: "Secure profile" },
          ].map(info => {
            const isLong = info.value && info.value.length > 6;
            return (
              <div key={info.label} onClick={info.onClick} style={{
                borderRadius: 22, background: "rgba(255,255,255,0.72)", backdropFilter: "blur(22px)",
                border: "1px solid rgba(255,255,255,0.8)", boxShadow: "0 14px 36px rgba(24,35,60,0.07)",
                padding: "18px 16px", cursor: info.onClick ? "pointer" : "default"
              }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 800, color: "#777b85", textTransform: "uppercase", letterSpacing: 1.4 }}>{info.label}</p>
                <h3 style={{ margin: "8px 0 4px", fontSize: isLong ? 15 : 26, fontWeight: 900, letterSpacing: isLong ? -0.2 : -0.8, lineHeight: 1.1 }}>{info.value}</h3>
                <p style={{ margin: 0, color: "#8a8e98", fontSize: 11 }}>{info.subtext}</p>
              </div>
            );
          })}
        </div>

        {/* Order Center */}
        <div style={card}>
          <SectionTitle title="Order Center" />
          <ActionRow title="Orders & Shipment Tracking" subtitle="View purchases, invoices, and live fulfillment updates" onClick={() => navigate("/account/orders")} />
          <ActionRow title="Returns & Support" subtitle="Start a return or contact ELEVEN support" onClick={() => navigate("/account/support")} />
        </div>

        {/* Personal Details */}
        <div style={card}>
          <SectionTitle title="Personal Details" />
          <DetailRow label="Full Name" value={fullName || "—"} />
          <DetailRow label="Email" value={user.email || "—"} />
          <DetailRow label="Phone" value={phoneFormatted} />
          <DetailRow label="Role" value={user.role || "customer"} />
        </div>

        {/* Account Settings */}
        <div style={card}>
          <SectionTitle title="Account Settings" />
          <ActionRow title="Shipping Addresses" subtitle="Manage delivery locations" onClick={() => navigate("/account/addresses")} />
          <ActionRow title="Payment Methods" subtitle="Manage checkout payment options" onClick={() => navigate("/account/payments")} />
          <ActionRow title="Security" subtitle="Password, sessions, and account protection" onClick={() => navigate("/account/security")} />
        </div>

        {/* Admin */}
        {user.role === "admin" && (
          <div style={{ borderRadius: 24, background: "linear-gradient(135deg,rgba(5,5,5,0.96),rgba(40,40,45,0.94))", color: "#fff", boxShadow: "0 24px 60px rgba(0,0,0,0.22)", padding: "22px 18px", marginBottom: 14 }}>
            <p style={{ margin: "0 0 4px", fontSize: 11, color: "#aaa", fontWeight: 900, letterSpacing: 2.2 }}>ADMIN ACCESS</p>
            <h3 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 850 }}>ELEVEN Control Center</h3>
            <p style={{ margin: "0 0 16px", color: "#bfc2ca", fontSize: 13 }}>Manage listings, inventory, orders, and marketplace operations.</p>
            <button onClick={() => navigate("/admin")} style={{ border: "none", background: "#fff", color: "#000", borderRadius: 999, padding: "12px 20px", fontSize: 14, fontWeight: 850, cursor: "pointer" }}>
              Open Admin
            </button>
          </div>
        )}

        <p style={{ textAlign: "center", color: "#8a8e98", fontSize: 13, marginTop: 8 }}>
          Your ELEVEN profile details are private and securely stored.
        </p>

      </div>
    </div>
  );
}